import { Button, Skeleton } from '@longy/web-components'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { CalendarOff, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { BookingNotice } from '../../components/booking/BookingNotice'
import { BookingStep } from '../../components/booking/BookingStep'
import { BookingSteps, SelectedValue } from '../../components/booking/BookingSteps'
import { SlotGroups } from '../../components/booking/SlotGroups'
import { api } from '../../core/trpc'
import { useLocale } from '../../hooks/useLocale'
import { formatPrice } from '../../utils/formatPrice'

const PATH = '/booking/examination'

/**
 * Only entries that lead to a calendar with something in it. Opt-in on the API: the app lists
 * services whose calendar is empty today on purpose, and this must not change that.
 *
 * Which services belong on the web is decided per service by `isWebVisible`, which every one of
 * these procedures already applies from the request — there is nothing to ask for here.
 */
const BOOKABLE_CATALOG = { withBookableSlotsOnly: true } as const

const examinationSearchSchema = z.object({
    /** Set when arriving from the catalogue, which has already picked the service. */
    serviceId: z.string().optional(),
})

const ExaminationBookingPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { getLocalizedValue } = useLocale()
    const { serviceId: prefilledServiceId } = Route.useSearch()

    const [serviceId, setServiceId] = useState<string | undefined>(prefilledServiceId)
    const [expertId, setExpertId] = useState<string | undefined>()
    const [slotId, setSlotId] = useState<string | undefined>()

    const {
        data: services,
        isPending: isServicesPending,
        isError: isServicesError,
        refetch: refetchServices,
    } = api.careExamination.getServicesForUser.useQuery(BOOKABLE_CATALOG)

    const { data: experts, isPending: isExpertsPending } =
        api.careExamination.getExpertsForUser.useQuery(
            { serviceId: serviceId ?? '', ...BOOKABLE_CATALOG },
            { enabled: !!serviceId },
        )

    const { data: slotGroups, isPending: isSlotsPending } =
        api.videoSlot.getGroupedSlotsForUser.useQuery(
            {
                expertId: expertId ?? '',
                serviceId,
                // Outpatient care has no ServiceType of its own — it is an EVENT carrying the flag.
                serviceType: 'EVENT' as const,
                isCareOrExamination: true,
            },
            { enabled: !!expertId && !!serviceId },
        )

    const selectedService = services?.find((service) => service.id === serviceId)
    const selectedExpert = experts?.find((expert) => expert.id === expertId)

    const slots = (slotGroups ?? []).flatMap((group) => group.slots)
    const selectedSlot = slots.find((slot) => slot.id === slotId)
    const purchasableItemId = selectedSlot?.purchasableItem?.id

    const step = !serviceId ? 1 : !expertId ? 2 : 3

    /*
     * A failed query settles the same way an empty one does — not pending, no data — so the error
     * has to be answered first or a visitor is told nothing is bookable when the request simply
     * never arrived.
     */
    if (isServicesError) {
        return (
            <BookingStep
                title={t('frontend.booking.outpatient')}
                step={t('frontend.booking.title')}
            >
                <BookingNotice
                    icon={TriangleAlert}
                    title={t('frontend.booking.loadFailedTitle')}
                    description={t('frontend.booking.loadFailedLead')}
                    action={{
                        label: t('frontend.booking.tryAgain'),
                        onClick: () => void refetchServices(),
                    }}
                />
            </BookingStep>
        )
    }

    /*
     * Nothing is bookable at all, so there is no step to be on: the indicator and the "choose a
     * service" heading would only be furniture around an apology. The later steps keep their chrome
     * instead, because by then the visitor has a chosen service or expert to back out of.
     */
    if (!isServicesPending && !services?.length) {
        return (
            <BookingStep
                title={t('frontend.booking.outpatient')}
                step={t('frontend.booking.title')}
            >
                <BookingNotice
                    icon={CalendarOff}
                    title={t('frontend.booking.noBookableTitle')}
                    description={t('frontend.booking.noBookableExaminationLead')}
                />
            </BookingStep>
        )
    }

    return (
        <BookingStep title={t('frontend.booking.outpatient')} step={t('frontend.booking.title')}>
            <BookingSteps current={step} />

            <div className="flex flex-col gap-6">
                {selectedService && (
                    <SelectedValue
                        label={t('frontend.steps.selectedService')}
                        value={getLocalizedValue(selectedService.name)}
                        changeLabel={t('frontend.steps.change')}
                        onChange={() => {
                            setServiceId(undefined)
                            setExpertId(undefined)
                            setSlotId(undefined)
                        }}
                    />
                )}
                {selectedExpert && (
                    <SelectedValue
                        label={t('frontend.steps.selectedExpert')}
                        value={selectedExpert.name}
                        changeLabel={t('frontend.steps.change')}
                        onChange={() => {
                            setExpertId(undefined)
                            setSlotId(undefined)
                        }}
                    />
                )}

                {step === 1 && (
                    <section className="flex flex-col gap-3">
                        <h2 className="text-lg font-semibold">
                            {t('frontend.booking.chooseService')}
                        </h2>
                        {isServicesPending ? (
                            <Skeleton className="h-40 w-full" />
                        ) : (
                            <ul className="grid gap-3 sm:grid-cols-2">
                                {services.map((service) => (
                                    <li
                                        key={service.id}
                                        className="flex flex-col rounded-lg border border-slate-200 bg-white transition-colors hover:border-primary"
                                    >
                                        <button
                                            type="button"
                                            className="w-full flex-1 p-4 text-left"
                                            onClick={() => setServiceId(service.id)}
                                        >
                                            <span className="block font-semibold">
                                                {getLocalizedValue(service.name)}
                                            </span>
                                            {service.price != null && (
                                                <span className="block text-sm text-primary">
                                                    {formatPrice(service.price)}
                                                </span>
                                            )}
                                        </button>
                                        {!service.hidden && (
                                            <Link
                                                className="self-start px-4 pb-4 text-sm font-medium text-brand-gray underline-offset-4 transition-colors hover:text-primary hover:underline"
                                                to="/care-examinations/$serviceId"
                                                params={{ serviceId: service.id }}
                                            >
                                                {t('frontend.catalog.details')}
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                )}

                {step === 2 && (
                    <section className="flex flex-col gap-3">
                        <h2 className="text-lg font-semibold">
                            {t('frontend.booking.chooseExpert')}
                        </h2>
                        {isExpertsPending ? (
                            <Skeleton className="h-40 w-full" />
                        ) : !experts?.length ? (
                            <p className="text-brand-gray">
                                {t('frontend.booking.noExpertsAvailable')}
                            </p>
                        ) : (
                            <ul className="grid gap-3 sm:grid-cols-2">
                                {experts.map((expert) => (
                                    <li key={expert.id}>
                                        <button
                                            type="button"
                                            className="w-full rounded-lg border border-slate-200 bg-white p-4 text-left transition-colors hover:border-primary"
                                            onClick={() => setExpertId(expert.id)}
                                        >
                                            <span className="block font-semibold">
                                                {expert.name}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                )}

                {step === 3 && (
                    <section className="flex flex-col gap-4">
                        <h2 className="text-lg font-semibold">
                            {t('frontend.booking.chooseSlot')}
                        </h2>
                        {isSlotsPending ? (
                            <Skeleton className="h-96 w-full" />
                        ) : (slotGroups ?? []).length === 0 ? (
                            <p className="text-brand-gray">
                                {t('frontend.booking.noSlotsAvailable')}
                            </p>
                        ) : (
                            <>
                                <SlotGroups
                                    groups={slotGroups ?? []}
                                    selectedSlotId={slotId}
                                    onSelect={setSlotId}
                                />
                                <Button
                                    className="self-end"
                                    disabled={!purchasableItemId}
                                    onClick={() =>
                                        purchasableItemId &&
                                        navigate({
                                            to: '/booking/checkout',
                                            search: { purchasableItemId },
                                        })
                                    }
                                >
                                    {t('frontend.booking.continueToPayment')}
                                </Button>
                            </>
                        )}
                    </section>
                )}
            </div>
        </BookingStep>
    )
}

export const Route = createFileRoute(PATH)({
    validateSearch: examinationSearchSchema,
    head: () => ({ meta: [{ title: 'Long-Y - Vizsgálat foglalása' }] }),
    component: ExaminationBookingPage,
})
